import AddIcon from "@atlaskit/icon/core/add";
import UploadIcon from "@atlaskit/icon/core/upload";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, notFound, useNavigate} from "@tanstack/react-router";
import {useState} from "react";
import {AssigneeDropdownCell} from "~/components/btlActivityComponents/assigneeDropdownCell";
import {CreateAllModal} from "~/components/btlActivityComponents/createAllModal";
import {CsvUploadComponent} from "~/components/btlActivityComponents/csvUploadComponent";
import {OttopilotColumnType} from "~/components/ottopilotDataViewer/column";
import {OttopilotDataViewer} from "~/components/ottopilotDataViewer/ottopilotDataViewer";
import {useOttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {ViewType} from "~/components/ottopilotDataViewer/view";
import {TableOnlyPage} from "~/components/tableOnlyPage";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import {
    BtlActivityTemplates,
    btlActivityTemplateToDisplayName,
    btlReviewItemApiResponseArraySchema,
    BtlReviewStatus,
    btlReviewStatusToDisplayName,
    type BtlReviewItem,
    type BtlReviewItemApiResponseItem,
} from "~/domainModels/btlActivities";
import {
    addBtlReviewItems,
    clearBtlReviewItems,
    getBtlReviewColumnValues,
    getBtlReviewItems,
    getBtlReviewItemsCount,
    getBtlReviewValidationSummary,
} from "~/domainModels/btlReviewStore";
import {transformApiResponseToDatabaseItems, transformDatabaseItemToReviewItem} from "~/domainModels/btlReviewTransformers";
import {DHIRAAGU__OU__ISLANDS__NAME__COLUMN_ID} from "~/domainModels/workspaceSpecific/dhiraagu";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeSimpleTag} from "@vesta/ananke/components/anankeSimpleTag";
import {AnankeTooltip} from "@vesta/ananke/components/anankeTooltip";
import type {Integer, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {formatDate, getUserDisplayName} from "~/utilities/utilities";
import {uniqueUuid} from "@vesta/unique/unique";
import {useShowErrorFlag} from "~/hooks/useShowErrorFlag";

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/btl-activities/review/$reviewId",
)({
    loader: async ({params}) => {
        const reviewIdResult = uuidZodValidator.safeParse(params.reviewId);
        if (reviewIdResult.success !== true) {
            throw notFound();
        }
        const reviewId = reviewIdResult.data;

        return {reviewId: reviewId};
    },
    component: RouteComponent,
});

// const BTL_CSV_TEMPLATE_CONFIG = {
//     headers: [
//         "row_id",
//         "title",
//         "type",
//         "timezone",
//         "start_date",
//         "start_time",
//         "end_date",
//         "end_time",
//         "all_day",
//         "geography_country",
//         "geography_atoll",
//         "geography_island",
//     ],
//     sampleData: [
//         "1",
//         "Island Visit – Kaafu",
//         "Door-to-Door",
//         "Asia/Kolkata",
//         "05-01-2026",
//         "10:00",
//         "05-01-2026",
//         "11:00",
//         "FALSE",
//         "Maldives",
//         "Kaafu",
//         "Hulhumale",
//     ],
//     fileName: "btl_activity_template.csv",
// };

const BTL_CSV_TEMPLATE_CONFIG = {
    headers: ["Title", "Description", "Timezone", "Start Time", "Start Date", "End Time", "End Date", "Is Allday Event", "Geography Nodes", "Type"],
    sampleData: ["First BTL", "First door to door btl", "Asia/Baghdad", "13:00", "17-01-2026", "15:00", "18-01-2026", "TRUE", "Male' City", "Door To Door"],
    fileName: "btl_activity_template.csv",
};

function RouteComponent() {
    const {reviewId} = Route.useLoaderData();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser} = useWorkspaceUser();
    const users = useUsers();
    const organizationalUnits = useOrganizationalUnits();
    const {workspace} = useWorkspace();
    const userTemplates = useUserTemplates();
    const queryClient = useQueryClient();
    const showErrorFlag = useShowErrorFlag();

    const [openCreateAllModal, setOpenCreateAllModal] = useState<boolean>(false);
    const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    // Query to check if all items have valid status
    const validationSummaryQuery = useQuery({
        queryKey: ["ottopilotDataViewer", uniqueUuid`a1b2c3d4-5e6f-7g8h-9i0j-k1l2m3n4o5p6`, "validationSummary"],
        queryFn: () => getBtlReviewValidationSummary(reviewId),
        staleTime: Infinity,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    const isCreateAllEnabled = validationSummaryQuery.data?.allValid === true;
    const invalidCount = validationSummaryQuery.data?.invalidCount ?? 0;
    const totalCount = validationSummaryQuery.data?.totalCount ?? 0;

    const handleReuploadCsv = async (file: File): Promise<Result<null>> => {
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("workspaceId", workspace.id);
            const result = await robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/api/csv/upload/btl-activities`,
                {
                    method: "POST",
                    body: formData,
                    headers: {Authorization: `Bearer ${heimdallUser.accessToken}`},
                },
                btlReviewItemApiResponseArraySchema,
            );

            if (result.success === false) {
                showErrorFlag(result.error.message);
                return errorResult(result.error);
            }

            // Cast validated data to the branded type - safe because zod validated the structure
            const apiResponse = result.data as Array<BtlReviewItemApiResponseItem>;

            // Clear existing items for this review session
            await clearBtlReviewItems(reviewId);

            // Transform and store new data in IndexedDB
            const dbItems = transformApiResponseToDatabaseItems(apiResponse, reviewId);
            await addBtlReviewItems(dbItems);

            // Invalidate queries to refresh the data
            // The actual query keys start with "ottopilotDataViewer" prefix (see ottopilotDataViewerController.tsx)
            await queryClient.resetQueries({
                queryKey: ["ottopilotDataViewer", uniqueUuid`a1b2c3d4-5e6f-7g8h-9i0j-k1l2m3n4o5p6`],
            });

            // Close modal
            setUploadModalOpen(false);
            setSelectedFile(null);

            return dataResult(null);
        } catch (err) {
            return errorResult(err instanceof Error ? err : new Error("An unexpected error occurred"));
        }
    };

    const btlReviewDataViewController = useOttopilotDataViewerController<BtlReviewItem>({
        rowDisplayNameVariants: {
            singularSentenceCase: "Event",
            pluralSentenceCase: "Events",
            singularLowerCase: "event",
            pluralLowerCase: "events",
        },
        queryKeyPrefix: [uniqueUuid`a1b2c3d4-5e6f-7g8h-9i0j-k1l2m3n4o5p6`],
        rowsQueryFn: async ({filterConfiguration, limit, offset, sortConfiguration, searchConfiguration}) => {
            const dbItems = await getBtlReviewItems(reviewId, {
                filterConfiguration: filterConfiguration,
                sortConfiguration: sortConfiguration,
                searchConfiguration: searchConfiguration,
                offset: offset,
                limit: limit,
            });

            return dbItems.map((item) => transformDatabaseItemToReviewItem(item, users));
        },
        rowCountQueryFn: async ({filterConfiguration, searchConfiguration}) => {
            const count = await getBtlReviewItemsCount(reviewId, filterConfiguration, searchConfiguration);
            return count as Integer;
        },
        columnValuesQueryFn: async ({columnId}) => {
            // Static values for enums
            if (columnId === "btlActivityTemplateId") {
                return Object.entries(BtlActivityTemplates).map((item) => item[1]);
            }
            if (columnId === "validationStatus") {
                return Object.values(BtlReviewStatus);
            }
            // Dynamic values from IndexedDB
            return await getBtlReviewColumnValues(reviewId, columnId);
        },
        columns: {
            revisionId: uniqueUuid`b2c3d4e5-6f7g-8h9i-0j1k-l2m3n4o5p6q7`,
            lineItems: [
                {
                    id: "__checkbox__",
                    type: OttopilotColumnType.string,
                    displayName: "",
                    valueGetter: () => "",
                    sortingCapability: false,
                    filteringCapability_contains: false,
                },
                {
                    id: "id",
                    type: OttopilotColumnType.uuid,
                    displayName: "ID",
                    valueGetter: (item) => item.id,
                },
                {
                    id: "title",
                    type: OttopilotColumnType.string,
                    displayName: "Event title",
                    valueGetter: (item) => item.title,
                    sortingCapability: true,
                    filteringCapability_contains: true,
                },
                {
                    id: "btlActivityTemplateId",
                    type: OttopilotColumnType.string,
                    displayName: "Event type",
                    valueGetter: (item) => item.btlActivityTemplateId ?? "",
                    valueFormatter: (value) => {
                        if (value === "") {
                            return "Unknown";
                        }
                        return btlActivityTemplateToDisplayName(value as BtlActivityTemplates);
                    },
                    cellRenderer: (row) => {
                        const templateId = row.btlActivityTemplateId;
                        const displayName = templateId !== null ? btlActivityTemplateToDisplayName(templateId) : "Unknown";
                        return (
                            <AnankeSimpleTag
                                appearance="default"
                                text={displayName}
                            />
                        );
                    },
                    sortingCapability: true,
                    filteringCapability_contains: true,
                },
                {
                    id: "validationStatus",
                    type: OttopilotColumnType.string,
                    displayName: "Validation status",
                    valueGetter: (item) => item.validationStatus,
                    valueFormatter: (value) => btlReviewStatusToDisplayName(value as BtlReviewStatus),
                    cellRenderer: (row) => {
                        const status = row.validationStatus;
                        const displayName = btlReviewStatusToDisplayName(status);
                        if (status === BtlReviewStatus.valid) {
                            return (
                                <div className="flex items-center gap-ds-space-050">
                                    <span>✓</span>
                                    <span>{displayName}</span>
                                </div>
                            );
                        }
                        return <span>{displayName}</span>;
                    },
                    sortingCapability: true,
                    filteringCapability_contains: true,
                },
                {
                    id: "validationErrors",
                    type: OttopilotColumnType.array,
                    displayName: "Validation errors",
                    valueGetter: (item) => (item.validationErrors.length > 0 ? item.validationErrors : ["None"]),
                    valueFormatter: (value) => (value.length > 0 ? value : []),
                    sortingCapability: true,
                    filteringCapability_contains: true,
                },
                {
                    id: "assignedUserIds",
                    type: OttopilotColumnType.array,
                    displayName: "Assignee(s)",
                    valueGetter: (item) => {
                        const allUserIds = item.assignedUsers.flatMap((group) => group.userIds);
                        return allUserIds.map((userId) => userId as string);
                    },
                    valueFormatter: (item) => {
                        return getUserDisplayName(item, users, userTemplates);
                    },
                    cellRenderer: (row) => {
                        return (
                            <AssigneeDropdownCell
                                row={row}
                                users={users}
                                userTemplates={userTemplates}
                                queryClient={queryClient}
                                queryKeyPrefix={[uniqueUuid`a1b2c3d4-5e6f-7g8h-9i0j-k1l2m3n4o5p6`]}
                            />
                        );
                    },
                    sortingCapability: true,
                    filteringCapability_contains: true,
                },
                {
                    id: "isAlldayEvent",
                    type: OttopilotColumnType.boolean,
                    displayName: "All day event",
                    valueGetter: (item) => item.isAlldayEvent,
                    valueFormatter: (value) => (value === true ? "True" : "False"),
                    sortingCapability: true,
                    filteringCapability_contains: true,
                },
                {
                    id: "startDate",
                    type: OttopilotColumnType.iso8601DateTime,
                    displayName: "Start date",
                    valueGetter: (item) => item.startDate,
                    valueFormatter: (value) => formatDate(value),
                    sortingCapability: true,
                    filteringCapability_contains: true,
                },
                {
                    id: "startTime",
                    type: OttopilotColumnType.string,
                    displayName: "Start time",
                    valueGetter: (item) => item.startTime ?? "",
                    sortingCapability: true,
                    filteringCapability_contains: true,
                },
                {
                    id: "endDate",
                    type: OttopilotColumnType.iso8601DateTime,
                    displayName: "End date",
                    valueGetter: (item) => item.endDate,
                    valueFormatter: (value) => formatDate(value),
                    sortingCapability: true,
                    filteringCapability_contains: true,
                },
                {
                    id: "endTime",
                    type: OttopilotColumnType.string,
                    displayName: "End time",
                    valueGetter: (item) => item.endTime ?? "",
                    sortingCapability: true,
                    filteringCapability_contains: true,
                },
                // {
                //     id: "region",
                //     type: OttopilotColumnType.array,
                //     displayName: "Region",
                //     valueGetter: (item) => item.region,
                //     sortingCapability: true,
                //     filteringCapability_contains: true,
                // },
                // {
                //     id: "atoll",
                //     type: OttopilotColumnType.array,
                //     displayName: "Atoll",
                //     valueGetter: (item) => item.atoll,
                //     sortingCapability: true,
                //     filteringCapability_contains: true,
                // },
                {
                    id: "island",
                    type: OttopilotColumnType.array,
                    displayName: "Island",
                    valueGetter: (item) => item.island,
                    valueFormatter: (ouIds: Array<string>) =>
                        Array.isArray(ouIds) && ouIds.length > 0
                            ? ouIds.map((ouId) =>
                                  String(
                                      organizationalUnits.find((ou) => ou.id === ouId)?.additionalColumnValues[DHIRAAGU__OU__ISLANDS__NAME__COLUMN_ID] ?? ouId,
                                  ),
                              )
                            : [],
                    sortingCapability: true,
                    filteringCapability_contains: true,
                },
            ],
        },
        views: [
            {
                id: uniqueUuid`c3d4e5f6-7g8h-9i0j-k1l2-m3n4o5p6q7r8`,
                viewType: ViewType.table,
                visibleColumns: [
                    // "__checkbox__",
                    "title",
                    "btlActivityTemplateId",
                    "validationStatus",
                    "validationErrors",
                    "assignedUsers",
                    "isAlldayEvent",
                    "startDate",
                    "assignedUserIds",
                    "island",
                ],
                otherArgs: {
                    rowSelection: "multiple",
                },
            },
        ],
        initialViewId: uniqueUuid`c3d4e5f6-7g8h-9i0j-k1l2-m3n4o5p6q7r8`,
        searchColumns: ["title"],
        // createPanelComponent: () => <div>TODO: Create</div>,
        // readPanelComponent: (row) => <div>TODO: Read {row.title}</div>,
        // updatePanelComponent: (row) => <div>TODO: Update {row.title}</div>,
    });
    const navigate = useNavigate();
    return (
        <>
            <TableOnlyPage
                breadcrumbs={["Beat planning", "Bulk upload"]}
                title="Review & edit events"
                titleRightSection={
                    <div className="grid grid-flow-col auto-cols-max gap-ds-space-100">
                        <AnankeTooltip
                            content={
                                isCreateAllEnabled === false
                                    ? totalCount === 0
                                        ? "No events to create. Please upload a CSV file first."
                                        : `${invalidCount} of ${totalCount} event${invalidCount === 1 ? " has" : "s have"} validation errors. Fix all errors before creating events.`
                                    : ""
                            }
                            position="bottom"
                        >
                            {(tooltipProps) => (
                                <AnankeButton
                                    {...tooltipProps}
                                    type="button"
                                    appearance="primary"
                                    isDisabled={isCreateAllEnabled === false}
                                    iconBefore={(iconProps) => (
                                        <AddIcon
                                            {...iconProps}
                                            label=""
                                        />
                                    )}
                                    onClick={() => setOpenCreateAllModal(true)}
                                >
                                    Create all
                                </AnankeButton>
                            )}
                        </AnankeTooltip>
                        <AnankeButton
                            type="button"
                            appearance="default"
                            iconBefore={(iconProps) => (
                                <UploadIcon
                                    {...iconProps}
                                    label=""
                                />
                            )}
                            onClick={() => setUploadModalOpen(true)}
                        >
                            Reupload CSV
                        </AnankeButton>
                    </div>
                }
                bottomSection={<OttopilotDataViewer controller={btlReviewDataViewController} />}
            />
            <CreateAllModal
                opened={openCreateAllModal}
                onClose={() => setOpenCreateAllModal(false)}
                reviewId={reviewId}
                onSuccess={() => {
                    navigate({
                        to: "/w/$workspaceId/ottopilot/btl-activities",
                        params: {workspaceId: workspace.id},
                        replace: true,
                    });
                }}
            />
            <CsvUploadComponent
                opened={uploadModalOpen}
                onClose={() => {
                    setUploadModalOpen(false);
                    setSelectedFile(null);
                }}
                selectedFile={selectedFile}
                onFileChange={setSelectedFile}
                templateConfig={BTL_CSV_TEMPLATE_CONFIG}
                onUploadAndReview={handleReuploadCsv}
                title="Reupload CSV"
                subtitle="Upload a new CSV file to replace the current data"
            />
        </>
    );
}
